import { GitAccountFormData, GitOAuthResponse } from '@common/git/GitIntegration/types';
import { ProjectDependencyType, DependencyTypes } from '@common/types/dependencyTypes';
import { AsyncEventRequest } from '@LSP/websocket/MdClient/queries';
import { ExtractArrayType } from '@prophecy/interfaces/generic';
import { BaseColor, Dialog } from '@prophecy/ui';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { parseJSON } from '@prophecy/utils/string';
import { useQueryClient } from '@tanstack/react-query';
import { ResultOf } from 'gql.tada';
import { nanoid } from 'nanoid';
import { useEffect, useMemo } from 'react';

import { SQL_PROVIDER } from '../constants/sql';
import { isProphecyUser, useUser } from '../context/user';
import { notifyCopilotConfigUpdate, regenerateSQLEditor } from '../data/apis/api';
import {
  getGqlDefaultOptions,
  ReactQueryError,
  readGraphQlErrorMessage,
  useGraphQlMutation,
  useGraphQlMutationSubscription,
  useGraphQlQuery
} from '../data/util';
import {
  BackEndCodeLanguage,
  BackEndCodeLanguageType,
  BuildSystemType,
  DefaultTransformationEntityType,
  ExternalGitProvider,
  ForkModeType,
  VersionControlMode,
  VersionControlModeType,
  GitCombinedStorageModelType,
  GitProviderType,
  ExternalGitProviderType
} from '../graphqlTypes/enums';
import { Providers } from '../schema/types';
import { isProphecyLibsDependency } from './Dependency/components/util';
import { UPDATE_CANCEL } from './git/CodeGeneration/constants';
import { useCodeGenerationState } from './git/CodeGeneration/context';
import { gitCredDetails } from './git/GitIntegration/utils';
import { useMetadataReloadAction } from './ide/state';
import { ProjectBrowserQueryKeys } from './ProjectBrowser/types';
import { QueryKeys } from './queries/common';
import { GitQueryKeys } from './queries/GitQueryKeys';
import {
  addDefaultDependenciesMutation,
  addOrchestrationEndpointMutation,
  addOrUpdateCopilotLLMConfigMutation,
  addOrUpdateTeamProjectCreationTemplateMutation,
  addProjectDependencyMutation,
  ClusterProjectCreationDefaultsQuery,
  createProjectWithExternalGitEntityMutationQuery,
  createProjectWithoutExternalGitEntityMutationQuery,
  getCopilotLLMConfigQuery,
  getOrchestrationSettingsQuery,
  getProjectStatusConfigQuery,
  migrateToExternalGitMutation,
  packageSubscriberTeamsQuery,
  PackageSubscriberTeamsQueryResponse,
  ProjectDependenciesQuery,
  ProjectEntityVariables,
  removeDefaultDependenciesMutation,
  removeOrchestrationEndpointMutation,
  removeProjectDependencyMutation,
  setAnalysisEnabledMutation,
  setProjectStatusConfigMutation,
  subscriberTeamsQuery,
  TeamProjectCreationDefaultsQuery,
  teamProjectCreationTemplatesQuery,
  UpdateClusterProjectCreationDefaultsMutation,
  updateOrchestrationEndpointMutation,
  updateProjectDependenciesMutation,
  updateProjectDependencyMutation,
  UpdateTeamProjectCreationDefaultsMutation
} from './queries/project';
import { TeamAvailableDefaultDependenciesQuery, TeamAvailableProjectsQuery, UserTeamsQuery } from './queries/team';
import { Entity } from './types/Entity';

export type NameIdPair = {
  name: string;
  _id: string;
};

export const EXTERNAL_PROJECT_GIT_FIELDS = [
  'externalUriArg',
  'externalSubPath',
  'externalRepoEmail',
  'externalRepoUsername',
  'externalRepoAccessToken',
  'externalGitProvider',
  'userGitCredsUID'
] as const;

export type GitFormData = GitAccountFormData & {
  mainBranch: string;
  forkMode: ForkModeType;
  externalSubPath?: string;
  defaultDevelopmentBranch?: string;
};

export type ProjectDetailFormData = {
  name: string;
  description?: string;
  ownerUID: string;
  language: BackEndCodeLanguageType;
  backendLanguage?: BackEndCodeLanguageType;
  buildSystem?: BuildSystemType;
  providerType?: SQL_PROVIDER;
  versionControlMode?: string;
  isImport?: boolean;
  defaultTransformationEntity?: DefaultTransformationEntityType;
};

export function isProphecyPackage(teamName: string) {
  return teamName === 'ProphecyPackageHub';
}

export function useAvailableTeamProjects(
  onlyPublishedToPackageHub: boolean,
  language: BackEndCodeLanguageType,
  teamId?: string,
  sqlProviderType?: string,
  gitProviderType?: GitProviderType
) {
  const normalizedSqlProviderType =
    sqlProviderType === Providers.ProphecyManaged ? sqlProviderType : sqlProviderType?.toLowerCase();
  const variables = {
    uid: teamId as string,
    language,
    sqlProviderType: normalizedSqlProviderType,
    gitProviderType
  };
  const { data, ...rest } = useGraphQlQuery(
    [QueryKeys.Team, 'availableProjectDependencies', variables],
    TeamAvailableProjectsQuery,
    {
      enabled: Boolean(teamId)
    },
    variables
  );
  if (data) {
    const items = data?.Team?.availableProjectDependencies || [];
    const availableProjectDependencies = onlyPublishedToPackageHub
      ? items.filter((item) => item.publishedToPackageHub)
      : items;
    return {
      availableProjectDependencies,
      ...rest
    };
  }
  return {
    availableProjectDependencies: [],
    ...rest
  };
}

export function useAvailableTeamDefaultProjects(
  language: BackEndCodeLanguageType,
  teamId?: string,
  sqlProviderType?: string,
  gitProviderType?: string,
  enabled = true
) {
  const variables = { uid: teamId as string, language, sqlProviderType, gitProviderType };
  const { data, ...rest } = useGraphQlQuery(
    [QueryKeys.Team, 'availableDefaultDependencies', variables],
    TeamAvailableDefaultDependenciesQuery,
    {
      enabled: enabled && Boolean(teamId),
      queryKey: [QueryKeys.Team, 'availableDefaultDependencies', variables, sqlProviderType, gitProviderType]
    },
    variables
  );
  if (data) {
    const availableDefaultDependencies = data.Team?.availableDefaultDependencies || [];
    return {
      availableDefaultDependencies,
      ...rest
    };
  }
  return {
    availableDefaultDependencies: [],
    ...rest
  };
}

export function useCreateTeamProjectCreationDefaults(onSuccess?: () => void) {
  const { mutateAsync, isLoading } = useGraphQlMutation(UpdateTeamProjectCreationDefaultsMutation, {
    onSuccess: () => {
      onSuccess?.();
    }
  });

  const createTeamProjectCreationDefaults = async (variables: {
    teamUID: string;
    sqlProviderType?: string;
    defaultDevelopmentBranch?: string;
    defaultMainBranch?: string;
    forkMode?: ForkModeType;
    versionControlMode?: VersionControlModeType;
    gitProviderType: GitProviderType;
    language: BackEndCodeLanguage;
  }) => {
    const result = await mutateAsync(variables);
    return result.updateTeamProjectCreationDefaults;
  };
  return {
    createTeamProjectCreationDefaults,
    isLoading
  };
}

export function useClusterProjectCreationDefaults(onSuccess?: () => void) {
  const { mutateAsync } = useGraphQlMutation(ClusterProjectCreationDefaultsQuery, {
    onSuccess: () => {
      onSuccess?.();
    }
  });

  const getClusterProjectCreationDefaults = async () => {
    const result = await mutateAsync({});
    return result.ClusterProjectCreationDefaults;
  };

  return {
    getClusterProjectCreationDefaults
  };
}

export function useTeamProjectCreationDefaults(onSuccess?: () => void) {
  const { mutateAsync } = useGraphQlMutation(TeamProjectCreationDefaultsQuery, {
    onSuccess: () => {
      onSuccess?.();
    }
  });

  const getTeamProjectCreationDefaults = usePersistentCallback(async (teamUID: string) => {
    const result = await mutateAsync({ teamUID });
    return result.TeamProjectCreationDefaults as {
      language: BackEndCodeLanguage;
      gitProviderType: string;
      sqlProviderType: string;
      versionControlType: VersionControlMode;
      gitStorageModel: ForkModeType;
      defaultMainBranch: string;
      defaultDevelopmentBranch: string;
    };
  });

  return {
    getTeamProjectCreationDefaults
  };
}

export function useOrchestrationSettings() {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useGraphQlMutation(getOrchestrationSettingsQuery, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.OrchestrationSettings]);
    }
  });

  const getOrchestrationSettings = usePersistentCallback(async () => {
    const result = await mutateAsync({});
    return result.OrchestrationEndpoints;
  });

  return {
    getOrchestrationSettings,
    isLoading
  };
}

export function useUpdateOrchestrationEndpoint() {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useGraphQlMutation(updateOrchestrationEndpointMutation, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.OrchestrationSettings]);
    }
  });

  async function updateOrchestrationEndpoint(variables: {
    uid: string;
    endpointUrl: string;
    default: boolean;
    masterKeyType: string;
    masterKeyValue: string;
  }) {
    await mutateAsync(variables);
  }

  return { updateOrchestrationEndpoint, isLoading };
}

export function useDeleteOrchestrationEndpoint() {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useGraphQlMutation(removeOrchestrationEndpointMutation, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.OrchestrationSettings]);
    }
  });

  async function deleteOrchestrationEndpoint(variables: { uid: string }) {
    await mutateAsync(variables);
  }

  return { deleteOrchestrationEndpoint, isLoading };
}

export function useAddOrchestrationEndpoint() {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useGraphQlMutation(addOrchestrationEndpointMutation, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.OrchestrationSettings]);
    }
  });

  async function addOrchestrationEndpoint(variables: {
    endpointUrl: string;
    default: boolean;
    masterKeyType: string;
    masterKeyValue: string;
  }) {
    await mutateAsync(variables);
  }

  return { addOrchestrationEndpoint, isLoading };
}

export function useUpdateClusterProjectCreationDefaults(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useGraphQlMutation(UpdateClusterProjectCreationDefaultsMutation, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.ClusterProjectCreationDefaults]);
      onSuccess?.();
    }
  });

  async function updateClusterProjectCreationDefaults(variables: {
    language: BackEndCodeLanguage;
    gitProviderType: GitProviderType;
    sqlProviderType?: string;
    versionControlMode?: VersionControlModeType;
    forkMode?: ForkModeType;
    defaultMainBranch?: string;
    defaultDevelopmentBranch?: string;
  }) {
    await mutateAsync(variables);
  }

  return { updateClusterProjectCreationDefaults, isLoading };
}

export function useUpdateAnalysis() {
  const queryClient = useQueryClient();
  const analysisMutation = useGraphQlMutation(setAnalysisEnabledMutation, {
    onSuccess: () => {
      queryClient.invalidateQueries(GitQueryKeys.Info);
    }
  });
  const updateAnalysis = usePersistentCallback(async (uid: string, hasAnalysis: boolean) => {
    await analysisMutation.mutateAsync({ hasAnalysis, projectUID: uid });
  });
  return { updateAnalysis };
}

export function useCreateProject(
  onSuccess: (uid: string) => void,
  onError: (message: string) => void,
  onProgress?: AsyncEventRequest['onProgress']
) {
  const queryClient = useQueryClient();
  const userEmail = useUser().user?.email || '';
  const { updateAnalysis } = useUpdateAnalysis();
  const mutationWithExternalGit = useGraphQlMutationSubscription(
    createProjectWithExternalGitEntityMutationQuery,
    undefined,
    onProgress
  );
  const mutationWithoutExternalGit = useGraphQlMutationSubscription(
    createProjectWithoutExternalGitEntityMutationQuery,
    undefined,
    onProgress
  );

  const updateOnSuccessCall = usePersistentCallback(async (uid: string, hasAnalysisUpdate = false) => {
    if (hasAnalysisUpdate) {
      await updateAnalysis(uid, hasAnalysisUpdate);
    }
    queryClient.invalidateQueries([QueryKeys.Project, QueryKeys.CreateEntity, { uid }]);
    onSuccess(uid);
  });

  async function createProject(projectDetail: ProjectDetailFormData, gitDetail: GitFormData, dependencies?: string[]) {
    const { buildSystem, ...variables } = projectDetail;
    const { mainBranch, forkMode, externalSubPath, defaultDevelopmentBranch, externalUriArg, ...gitCredDetail } =
      gitDetail;
    const { externalGitProvider } = gitCredDetail;

    const data = {
      ...variables,
      mainBranch,
      forkMode,
      defaultDevelopmentBranch,
      externalSubPath,
      externalUriArg,
      ...gitCredDetails(gitCredDetail as GitOAuthResponse)
    } as ProjectEntityVariables;

    const isProphecyManaged = externalGitProvider === ExternalGitProvider.ProphecyManaged;
    if (isProphecyManaged) {
      EXTERNAL_PROJECT_GIT_FIELDS.forEach((gitField) => {
        delete data[gitField];
      });
    }

    if (dependencies?.length) {
      data.dependencyProjectUIDs = dependencies;
    }
    function _onError(errorResponse: ReactQueryError) {
      const message = readGraphQlErrorMessage(errorResponse);
      onError(message);
    }
    function _onSuccess(resp: ResultOf<typeof createProjectWithoutExternalGitEntityMutationQuery>) {
      const uid = resp.addProject._id;
      updateOnSuccessCall(uid, isProphecyUser(userEmail) && projectDetail.language === BackEndCodeLanguage.sql);
    }
    if (isProphecyManaged) {
      await mutationWithoutExternalGit.mutateAsync(data, {
        onError: _onError,
        onSuccess: _onSuccess
      });
    } else {
      await mutationWithExternalGit.mutateAsync(data, {
        onError: _onError,
        onSuccess: _onSuccess
      });
    }
  }

  return {
    createProject,
    isLoading: mutationWithExternalGit.isLoading || mutationWithoutExternalGit.isLoading
  };
}

export function useAddProjectDependency() {
  const client = useQueryClient();
  const { mutateAsync, ...rest } = useGraphQlMutation(addProjectDependencyMutation, {
    onSuccess: () => {
      client.invalidateQueries([QueryKeys.Project]);
      client.invalidateQueries(ProjectBrowserQueryKeys.ProjectBrowser);
    }
  });

  const {
    actions: { handleRebuildCode },
    isLoading
  } = useCodeGenerationState();

  async function addProjectDependency(projectId: string, dependencyProjectUID: string, releaseTag?: string) {
    const handleCallback = async () => await mutateAsync({ projectId, dependencyProjectUID, releaseTag });
    return await handleRebuildCode({ handleCallback, title: 'Add Dependency' });
  }

  return { addProjectDependency, ...rest, isLoading: isLoading || rest.isLoading };
}

export function useUpdateProjectDependencies() {
  const client = useQueryClient();
  const { reloadMetadataOrReinitializeConnection } = useMetadataReloadAction();

  const { mutateAsync, ...rest } = useGraphQlMutation(updateProjectDependenciesMutation, {
    onSuccess: async (data) => {
      client.invalidateQueries([QueryKeys.Project]);
      client.invalidateQueries(ProjectBrowserQueryKeys.ProjectBrowser);
      await reloadMetadataOrReinitializeConnection();
      const dependencylistFailedToUpdate = data.updateProjectAllDependencies;
      const count = dependencylistFailedToUpdate.length;
      if (count > 0) {
        Dialog.alert({
          'data-test-id': 'update-dependencies-alert-dialog',
          title: 'Update Dependencies',
          children: `${count} ${count > 1 ? 'dependencies' : 'dependency'} update failed.}`
        });
      } else {
        Dialog.success({
          'data-test-id': 'update-dependencies-success-dialog',
          title: 'Update Dependencies',
          children: 'All dependencies updated successfully.'
        });
      }
    }
  });

  const {
    actions: { handleRebuildCode },
    isLoading
  } = useCodeGenerationState();

  async function updateProjectDependencies(projectId: string) {
    const handleCallback = async () => await mutateAsync({ projectId });
    return await handleRebuildCode({ handleCallback, title: 'Modifying Project Dependencies', disabledLostText: true });
  }

  return { updateProjectDependencies, ...rest, isLoading: rest.isLoading || isLoading };
}

export function useUpdateProjectDependency() {
  const client = useQueryClient();
  const { mutateAsync, ...rest } = useGraphQlMutation(updateProjectDependencyMutation, {
    onSuccess: () => {
      client.invalidateQueries([QueryKeys.Project]);
      client.invalidateQueries(ProjectBrowserQueryKeys.ProjectBrowser);
    }
  });

  const {
    actions: { handleRebuildCode },
    isLoading
  } = useCodeGenerationState();

  async function updateProjectDependency(projectId: string, dependencyProjectUID: string, releaseTag: string) {
    const handleCallback = async () => await mutateAsync({ projectId, dependencyProjectUID, releaseTag });
    return await handleRebuildCode({ handleCallback, title: 'Modifying Project Dependencies', disabledLostText: true });
  }

  return { updateProjectDependency, ...rest, isLoading: rest.isLoading || isLoading };
}

const removeProjectDependencyMutationName = 'removeProjectDependencyMutation';
const options = getGqlDefaultOptions(undefined, removeProjectDependencyMutationName);

export function useRemoveProjectDependency(onSuccess: () => void, onError: () => void) {
  const client = useQueryClient();
  const { mutateAsync, ...rest } = useGraphQlMutation(removeProjectDependencyMutation, {
    onSuccess: () => {
      client.invalidateQueries([QueryKeys.Project]);
      client.invalidateQueries(ProjectBrowserQueryKeys.ProjectBrowser);
      onSuccess();
    },
    onError: (error) => {
      options.onError(error as ReactQueryError);
      onError();
    }
  });

  const {
    actions: { handleRebuildCode },
    isLoading
  } = useCodeGenerationState();

  async function removeProjectDependency(
    projectId: string,
    dependencyProjectUID: string,
    defaultText: string,
    callBack: () => Promise<unknown>
  ) {
    const handleCallback = async () => {
      const allowed = await callBack();
      if (!allowed) {
        return UPDATE_CANCEL;
      }
      return await mutateAsync({ projectId, dependencyProjectUID });
    };
    return await handleRebuildCode({
      handleCallback,
      title: 'Delete Dependency',
      saveBtn: 'Delete',
      warningText: defaultText,
      disabledLostText: true
    });
  }

  return { removeProjectDependency, ...rest, isLoading: rest.isLoading || isLoading };
}

export type CopilotLLMConfig = {
  AIModelProvidersCreds: string;
  availableAIModels: string;
  availableAISpeechModels: string;
};

export function getDefaultCopilotLLMConfig(): CopilotLLMConfig {
  return {
    AIModelProvidersCreds: '{}',
    availableAIModels: '{}',
    availableAISpeechModels: '{}'
  };
}

export function useAddOrUpdateCopilotLLMConfig(onSuccess: () => void) {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useGraphQlMutation(addOrUpdateCopilotLLMConfigMutation, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.CopilotLLMConfig]);
      notifyCopilotConfigUpdate();
      onSuccess?.();
    }
  });

  async function addOrUpdateCopilotLLMConfig(variables: CopilotLLMConfig) {
    await mutateAsync(variables);
  }

  return { addOrUpdateCopilotLLMConfig, ...rest, isLoading: rest.isLoading };
}

export function useGetCopilotLLMConfig() {
  const { data } = useGraphQlQuery([QueryKeys.CopilotLLMConfig], getCopilotLLMConfigQuery, { enabled: true });

  return {
    data: data?.getCopilotLLMConfig as CopilotLLMConfig | undefined
  };
}

export function useProjectDependencies(projectId: string, enabled = true) {
  const { data, ...rest } = useGraphQlQuery(
    [QueryKeys.Project, 'ProjectDependencies', { projectId }],
    ProjectDependenciesQuery,
    { enabled: Boolean(enabled && projectId) },
    { projectId }
  );
  const project = useMemo(() => data?.Project, [data?.Project]);
  const projectInfo = useMemo(() => project?.projectDependencyInfo, [project?.projectDependencyInfo]);
  const projectDependencies = useMemo(() => {
    const projectDependencies: ProjectDependencyType[] = [];
    projectInfo?.dependencies.forEach(({ version, directDependency, project }) => {
      const dependency: ProjectDependencyType = {
        directDependency,
        version,
        type: isProphecyPackage(project.team.name) ? DependencyTypes.system : DependencyTypes.package,
        entity: Entity.Project,
        id: project._id,
        name: project.name,
        //Has to convert it to string as metadata sending number while IDE expecting string need to sync both things on backend
        createdAt: project.created as unknown as string,
        latestReleaseVersion: project.latestReleaseVersion as string,
        description: project.description
      };
      projectDependencies.push(dependency);
    });
    return projectDependencies;
  }, [projectInfo?.dependencies]);
  const defaultJson = useMemo(() => ({ dependencies: '[]' }), []);
  const jsonValue = useMemo(
    () =>
      project?.dependencies
        ? parseJSON(
            parseJSON(project.dependencies, defaultJson)?.dependencies || defaultJson.dependencies,
            defaultJson.dependencies
          )
        : [],
    [defaultJson, project?.dependencies]
  );
  const libraryDependencies: ProjectDependencyType[] = useMemo(
    () =>
      (typeof jsonValue === 'object' ? jsonValue : []).map((dependency: ProjectDependencyType) => ({
        ...dependency,
        enabled: true,
        entity: Entity.Project,
        id:
          dependency.id ||
          `${isProphecyLibsDependency(dependency.type) ? dependency.name || dependency.type : `library_${nanoid(9)}`} `
      })),
    [jsonValue]
  );

  const projectData = useMemo(
    () => ({
      language: project?.language || BackEndCodeLanguage.sql,
      teamId: project?.team?._id,
      conflictingDependencies: projectInfo?.dependencyConflicts || [],
      projectDependencies,
      version: project?.latestReleaseVersion,
      libraryDependencies
    }),
    [project, projectInfo, projectDependencies, libraryDependencies]
  );
  return { ...projectData, isLoading: rest.isLoading };
}

//PackageSubscriberTeamsQueryResponse
export function useProjectSubscriberTeams(projectId: string) {
  return useGraphQlQuery(
    [QueryKeys.Project, 'project-subscriberTeams', { projectId }],
    subscriberTeamsQuery,
    { enabled: Boolean(projectId) },
    {
      projectId
    }
  );
}

export function usePackageSubscriberTeams(projectId: string) {
  return useGraphQlQuery<PackageSubscriberTeamsQueryResponse>(
    [QueryKeys.Project, 'package-subscriberTeams', { projectId }],
    // @ts-ignore FIXME: remove once gql schema types are working
    packageSubscriberTeamsQuery,
    { enabled: Boolean(projectId) },
    {
      projectId
    }
  );
}

export function useUserTeams(enabled = true) {
  const { user } = useUser();
  const uid = user?.id;
  const variables = { uid: uid };
  const { data: teamData, ...rest } = useGraphQlQuery(
    [QueryKeys.User, QueryKeys.TeamList, variables],
    UserTeamsQuery,
    { enabled: Boolean(uid) && enabled },
    variables
  );

  const teams = teamData?.User?.teams;
  const defaultTeams = FROZEN_EMPTY_ARRAY as ExtractArrayType<NonNullable<typeof teams>>[];
  return { userId: uid, data: teams || defaultTeams, ...rest };
}

export function useSQLProjectRegenerate(projectId: string, enabled: boolean, stateBranches: string[]) {
  useEffect(() => {
    // for non sql project or onboarding project no need to regenerate
    if (enabled) {
      stateBranches.forEach((branch) => {
        regenerateSQLEditor({ projectId, branch });
      });
    }
  }, [enabled, projectId, stateBranches]);
}

export function useRemoveDefaultProject(onSuccess: () => void, onError: () => void) {
  const client = useQueryClient();
  const { mutateAsync, ...rest } = useGraphQlMutation(removeDefaultDependenciesMutation, {
    onSuccess: () => {
      client.invalidateQueries([QueryKeys.Team]);
      onSuccess();
    },
    onError: (error) => {
      options.onError(error as ReactQueryError);
      onError();
    }
  });

  async function removeDefaultProject(teamUID: string, projectUIDs: number[]) {
    return mutateAsync({ teamUID, projectUIDs });
  }

  return { removeDefaultProject, ...rest };
}

export function useAddDefaultProject() {
  const client = useQueryClient();
  const { mutateAsync, ...rest } = useGraphQlMutation(addDefaultDependenciesMutation, {
    onSuccess: () => {
      client.invalidateQueries([QueryKeys.Team]);
    }
  });

  async function addDefaultProject(teamUID: string, projectUIDs: number[]) {
    return mutateAsync({ teamUID, projectUIDs });
  }

  return { addDefaultProject, ...rest };
}

export function useAddOrUpdateTeamProjectCreationTemplate(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useGraphQlMutation(addOrUpdateTeamProjectCreationTemplateMutation, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.ProjectCreationTemplate]);
      queryClient.refetchQueries([QueryKeys.ProjectCreationTemplate]);
      onSuccess?.();
    }
  });

  async function addOrUpdateTeamProjectCreationTemplate(variables: {
    uid?: string;
    teamUID: string;
    name: string;
    isDefault: boolean;
    allowToCustomize: boolean;
    language: BackEndCodeLanguageType;
    gitProviderType: GitProviderType;
    gitCombinedStorageModel: GitCombinedStorageModelType;
    sqlProviderType?: string;
    defaultMainBranch?: string;
    defaultDevelopmentBranch?: string;
    defaultTransformationEntity?: DefaultTransformationEntityType;
  }) {
    await mutateAsync(variables);
  }

  return { addOrUpdateTeamProjectCreationTemplate, ...rest, isLoading: rest.isLoading };
}

export interface DefaultProjectFormData {
  language?: BackEndCodeLanguageType;
  gitProviderType: GitProviderType;
  sqlProviderType?: string;
  versionControlType?: VersionControlMode;
  gitCombinedStorageModel: GitCombinedStorageModelType;
  defaultMainBranch?: string;
  defaultDevelopmentBranch?: string;
  name: string;
  isDefault: boolean;
  uid?: number;
  allowToCustomize: boolean;
  defaultTransformationEntity?: DefaultTransformationEntityType;
}

export function useGetTeamsTemplates() {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useGraphQlMutation(teamProjectCreationTemplatesQuery, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.ProjectCreationTemplate]);
    }
  });
  const getTeamsTemplates = usePersistentCallback(async (teamId: string) => {
    return mutateAsync({ teamUID: teamId });
  });

  return { getTeamsTemplates, ...rest, isLoading: rest.isLoading };
}

export function useGetTeamProjectCreationTemplates(teamId: string) {
  return useGraphQlQuery(
    [QueryKeys.ProjectCreationTemplate, 'TeamProjectCreationTemplates', { teamUID: teamId }],
    teamProjectCreationTemplatesQuery,
    { enabled: Boolean(teamId) },
    { teamUID: teamId }
  );
}

export type ProjectStatusType = {
  entryName: string;
  colour: string;
  isAdminEntry: boolean;
  emailEnabled: boolean;
};

export function useGetProjectStatusConfig() {
  const { data, isLoading, ...rest } = useGraphQlQuery([QueryKeys.ProjectStatusConfig], getProjectStatusConfigQuery);
  return {
    data: data?.getProjectStatusConfig,
    isLoading,
    ...rest
  };
}

export function useSetProjectStatusConfig(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useGraphQlMutation(setProjectStatusConfigMutation, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.ProjectStatusConfig]);
      onSuccess?.();
    }
  });

  async function setProjectStatusConfig(projectStatusConfig: Array<ProjectStatusType>) {
    return mutateAsync({ projectStatusConfig });
  }

  return { setProjectStatusConfig, ...rest, isLoading: rest.isLoading };
}

export function useEvaluateCurrentStatus(currentProjectStatus?: string) {
  const { data: projectStatusConfig } = useGetProjectStatusConfig();
  const currentStatus = projectStatusConfig?.find((status) => status.entryName === currentProjectStatus);
  if (!currentStatus) {
    return {
      entryName: currentProjectStatus || 'Unknown',
      colour: BaseColor.gray,
      isAdminEntry: false,
      emailEnabled: false
    };
  }
  return currentStatus;
}

export function useMigrateToExternalGit(onSuccess?: (uid: string) => void, onError?: (message: string) => void) {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useGraphQlMutation(migrateToExternalGitMutation, {
    onError: (errorResponse) => {
      const message = readGraphQlErrorMessage(errorResponse);
      console.error(message);
      onError?.(message);
    },
    onSuccess: (resp) => {
      queryClient.invalidateQueries([QueryKeys.Project]);
      // Use the project ID from the form data since the response might not have the correct type
      onSuccess?.('');
    }
  });

  async function migrateToExternalGit(formData: {
    uid: string;
    externalSubPath?: string;
    externalUriArg?: string;
    externalRepoUsername?: string;
    externalRepoEmail?: string;
    externalRepoAccessToken?: string;
    defaultMainBranch?: string;
    defaultDevelopmentBranch?: string;
    copyMainBranchReleaseTags?: boolean;
    userGitCredsUID?: number;
    externalGitProvider?: ExternalGitProviderType;
    repositories?: Array<{ externalUriArg: string; repositoryName: string; mainBranch: string }>;
  }) {
    const {
      uid,
      externalSubPath,
      externalUriArg,
      defaultMainBranch,
      defaultDevelopmentBranch,
      copyMainBranchReleaseTags,
      externalGitProvider,
      ...gitAccountInfo
    } = formData;

    const gitCreds = gitCredDetails(gitAccountInfo as GitOAuthResponse);

    const variables = {
      uid,
      externalSubPath: externalSubPath || '',
      externalUriArg: externalUriArg || '',
      defaultMainBranch: defaultMainBranch || '',
      defaultDevelopmentBranch: defaultDevelopmentBranch || '',
      copyMainBranchReleaseTags: copyMainBranchReleaseTags || false,
      ...gitCreds
    };

    return mutateAsync(variables);
  }

  return { migrateToExternalGit, isLoading };
}
